import { Component, EventEmitter, Input, Output, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

export type LegalDocType = 'privacy' | 'terms' | 'cookies';

interface LegalSection {
  heading?: string;
  paragraphs?: string[];
  list?: string[];
}

interface LegalDoc {
  title: string;
  effectiveDate: string;
  sections: LegalSection[];
}

@Component({
  selector: 'app-legal-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './legal-modal.html',
  styleUrls: ['./legal-modal.scss']
})
export class LegalModal {

  /** Which document is currently open. Null = closed. */
  @Input() activeDoc: LegalDocType | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() docChange = new EventEmitter<LegalDocType>();

  readonly docs: Record<LegalDocType, LegalDoc> = {

    privacy: {
      title: 'Privacy Policy',
      effectiveDate: '16 June 2026',
      sections: [
        {
          paragraphs: [
            'Malkrís is committed to protecting privacy, maintaining confidentiality, and securing information across all areas of its operations.',
            'As a globally operating business headquartered in Chennai, India, we process information in accordance with applicable international and local data protection laws.'
          ]
        },
        {
          heading: 'Information We Collect',
          paragraphs: ['We may collect:'],
          list: [
            'Identity Information — Name, email address, phone number, company details, and business correspondence.',
            'Technical Information — IP address, browser type, device information, session logs, and system identifiers.',
            'Usage Information — Platform interactions, engagement patterns, operational inputs, and service activity.',
            'Business Data — Records, analytics inputs, uploaded materials, and workflow information processed through our services.'
          ]
        },
        {
          heading: 'How We Use Information',
          paragraphs: ['We process information to:'],
          list: [
            'Deliver, maintain, and improve our services',
            'Secure our systems and digital infrastructure',
            'Provide customer support and communications',
            'Enhance business intelligence and operational insights',
            'Meet contractual, regulatory, and legal obligations',
            'Detect fraud, abuse, or unauthorized activities'
          ]
        },
        {
          heading: 'Lawful Basis',
          paragraphs: [
            'Where required by law, we process personal data based on consent, contractual necessity, legitimate interests, or legal obligations.'
          ]
        },
        {
          heading: 'Data Sharing',
          paragraphs: [
            'We do not sell personal information.',
            'Information may only be shared with:'
          ],
          list: [
            'Authorized infrastructure and service providers',
            'Regulatory or legal authorities where required',
            'Internal affiliates and operational partners under confidentiality obligations'
          ]
        },
        {
          heading: 'International Data Transfers',
          paragraphs: [
            'Given our global operations, data may be processed or transferred across jurisdictions. Where this occurs, appropriate safeguards are implemented.'
          ]
        },
        {
          heading: 'Data Retention',
          paragraphs: [
            'We retain information only for as long as necessary to fulfill operational, contractual, security, and legal requirements.'
          ]
        },
        {
          heading: 'Data Security',
          paragraphs: [
            'We maintain commercially reasonable administrative, technical, and organizational safeguards, including access controls, encryption practices, monitoring systems, and secure operational protocols.',
            'While no system is entirely immune from risk, we continuously work to protect the integrity of all data under our control.'
          ]
        },
        {
          heading: 'Your Rights',
          paragraphs: ['Subject to applicable law, you may request:'],
          list: [
            'Access to your personal information',
            'Correction of inaccurate data',
            'Deletion of eligible information',
            'Restriction of processing',
            'Data portability',
            'Withdrawal of consent where applicable'
          ]
        },
        {
          heading: "Children's Privacy",
          paragraphs: ['Our services are not intended for individuals under the age of 18.']
        },
        {
          heading: 'Policy Updates',
          paragraphs: [
            'We may update this Privacy Policy from time to time to reflect legal, operational, or technological changes.'
          ]
        },
        {
          heading: 'Contact',
          paragraphs: ['For privacy-related matters: legal@malkris.com']
        }
      ]
    },

    terms: {
      title: 'Legal Notice & Terms of Use',
      effectiveDate: '16 June 2026',
      sections: [
        {
          paragraphs: [
            'By accessing or using Malkrís, you agree to these Terms of Use.',
            'Malkrís operates as a globally delivered business intelligence and infrastructure platform headquartered in Chennai, India.'
          ]
        },
        {
          heading: 'Use of Services',
          paragraphs: [
            'You agree to use our website and services lawfully, responsibly, and in accordance with these Terms.',
            'You shall not:'
          ],
          list: [
            'Misuse, disrupt, or interfere with our systems',
            'Attempt unauthorized access to any infrastructure',
            'Reverse-engineer, copy, replicate, or exploit proprietary systems',
            'Use our services for unlawful, fraudulent, or harmful purposes'
          ]
        },
        {
          heading: 'Intellectual Property',
          paragraphs: [
            'All content, systems, software, frameworks, trademarks, designs, methodologies, and proprietary materials are the exclusive property of Malkrís unless otherwise stated.',
            'Unauthorized use, reproduction, or distribution is strictly prohibited.'
          ]
        },
        {
          heading: 'Confidentiality',
          paragraphs: [
            'Any non-public information, systems, or materials accessed through Malkrís shall be treated as confidential and may not be disclosed or misused.'
          ]
        },
        {
          heading: 'Third-Party Services',
          paragraphs: [
            'Our services may integrate or rely on third-party technologies, infrastructure, or external systems.',
            'Malkrís is not responsible for third-party content, availability, or practices.'
          ]
        },
        {
          heading: 'Availability',
          paragraphs: [
            'We may modify, suspend, restrict, or discontinue any part of our services at any time without prior notice.'
          ]
        },
        {
          heading: 'Disclaimer',
          paragraphs: [
            'All services are provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied.'
          ]
        },
        {
          heading: 'Limitation of Liability',
          paragraphs: [
            'To the fullest extent permitted by law, Malkrís shall not be liable for indirect, incidental, consequential, special, or business-related damages arising from the use of its services.'
          ]
        },
        {
          heading: 'Indemnification',
          paragraphs: [
            'You agree to indemnify and hold harmless Malkrís from any claims, damages, liabilities, or expenses arising from misuse of the platform or violation of these Terms.'
          ]
        },
        {
          heading: 'Force Majeure',
          paragraphs: [
            'Malkrís shall not be liable for delays or failures caused by events beyond reasonable control, including technical failures, cyber incidents, natural events, governmental actions, or infrastructure disruptions.'
          ]
        },
        {
          heading: 'Termination',
          paragraphs: [
            'We reserve the right to suspend or terminate access where these Terms are violated or where operational integrity is at risk.'
          ]
        },
        {
          heading: 'Governing Law',
          paragraphs: [
            'These Terms shall be governed by the laws of India.',
            'Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Chennai, Tamil Nadu, India.'
          ]
        }
      ]
    },

    cookies: {
      title: 'Cookie Policy',
      effectiveDate: '16 June 2026',
      sections: [
        {
          paragraphs: [
            'At Malkrís, we use cookies and related technologies to maintain secure access, optimize platform performance, and improve the intelligence of our digital ecosystem.',
            'Our use of cookies is designed with a focus on security, performance, and operational integrity.'
          ]
        },
        {
          heading: 'How We Use Cookies',
          paragraphs: ['Cookies may be used to:'],
          list: [
            'Maintain secure sessions and user authentication',
            'Enable core website and platform functionality',
            'Analyze traffic, engagement, and operational behavior',
            'Improve performance, responsiveness, and system efficiency',
            'Detect risks, anomalies, or unauthorized access',
            'Preserve user preferences and enhance usability'
          ]
        },
        {
          heading: 'Categories of Cookies',
          list: [
            'Essential Cookies — Required for the operation, security, and stability of our website and services.',
            'Performance Cookies — Help us understand platform behavior and improve operational performance.',
            'Analytics Cookies — Allow us to evaluate usage patterns and improve user experience.',
            'Functional Cookies — Remember settings, preferences, and interaction history.',
            'Third-Party Cookies — May be deployed by integrated service providers supporting infrastructure, analytics, communications, or embedded services.'
          ]
        },
        {
          heading: 'Your Choices',
          paragraphs: [
            'You may control or disable cookies through your browser settings. Certain essential cookies are necessary for core functionality and cannot be disabled without affecting service performance.',
            'By continuing to use our website, you consent to the use of cookies in accordance with this Policy.'
          ]
        },
        {
          heading: 'Updates',
          paragraphs: [
            'We may update this Cookie Policy periodically to reflect operational, legal, or technological changes.'
          ]
        }
      ]
    }

  };

  get currentDoc(): LegalDoc | null {
    return this.activeDoc ? this.docs[this.activeDoc] : null;
  }

  switchTo(doc: LegalDocType): void {
    this.docChange.emit(doc);
  }

  close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.activeDoc) {
      this.close();
    }
  }
}